import { CSSProperties, InputHTMLAttributes } from 'react';

import clsx from 'clsx';

// Tailwind rebuild of the old shared Inputs-styled.tsx survivors. Colors map
// onto tailwind.config.js's existing gray/red/slate scales (confirmed by
// direct hex comparison against style/utilities/colors.scss, no new
// arbitrary values): theme.color.inputBgDefault (gray950) -> bg-gray-950;
// errorDefault (#E95C7B) -> red-400; inputBorderDisabled (gray800) ->
// gray-800; inputBorderDefault (gray500) -> gray-500; inputFgDisabled
// (gray700) -> gray-700; inputText (gray300) -> gray-300; NumberInput's
// non-error/non-disabled border (theme.color.slate400, #28303E) ->
// slate-600 -- Tailwind's slate scale is numbered differently than the
// scss tokens (confirmed by hex, not by matching key name: slate-400 is
// #868E9F, a much lighter gray -- slate-600 is the actual #28303E match).
interface StyledInputProps extends InputHTMLAttributes<HTMLInputElement> {
  error?: boolean;
}

const baseInputClasses = (error?: boolean, disabled?: boolean) =>
  clsx(
    'w-full min-w-0 flex-1 rounded-none bg-gray-950 pl-2 text-base font-normal leading-[1.5] focus:shadow-none',
    // Original TextInput's text color only ever depends on `disabled`
    // (theme.color.inputText otherwise) -- `error` only reddens the border.
    // NumberInput below is the one whose text also reddens on error, per its
    // own separate original styling.
    error && 'border border-red-400 text-gray-300',
    !error && disabled && 'border border-gray-800 text-gray-700',
    !error && !disabled && 'border border-gray-500 text-gray-300',
  );

export const TextInput = ({ error, disabled, className, ...props }: StyledInputProps) => (
  <input
    type="text"
    disabled={disabled}
    className={clsx(baseInputClasses(error, disabled), className)}
    {...props}
  />
);

// Only ever rendered internally by NumberInputWithUnits below (no direct
// external consumer) -- same border color as the original
// (theme.color.slate400 rather than the plain TextInput's gray500).
const NumberInput = ({ error, disabled, className, ...props }: StyledInputProps) => (
  <input
    type="number"
    disabled={disabled}
    className={clsx(
      'w-full min-w-0 flex-1 rounded-none bg-gray-950 pl-2 text-base font-normal leading-[1.5] focus:shadow-none',
      // Removes the native up/down spin buttons, same as the original.
      '[-moz-appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none',
      error && 'border border-red-400 text-red-400',
      !error && disabled && 'border border-gray-800 text-gray-700',
      !error && !disabled && 'border border-slate-600 text-gray-300',
      className,
    )}
    {...props}
  />
);

interface NumberInputWithUnitsProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  unit: string;
  leftAligned?: boolean;
  style?: CSSProperties;
  error: boolean;
}

export const NumberInputWithUnits = ({
  unit,
  leftAligned = false,
  style = {},
  error,
  disabled,
  ...props
}: NumberInputWithUnitsProps) => {
  const inputStyle: CSSProperties = {
    paddingRight: leftAligned ? '0px' : '26px',
    paddingLeft: leftAligned ? '26px' : '0px',
    textAlign: leftAligned ? 'left' : 'right',
    ...style,
  };

  const unitLabelClasses = clsx(
    'absolute top-1/2 -translate-y-1/2 px-0.5',
    error ? 'text-red-400' : disabled ? 'text-gray-700' : 'text-gray-300',
  );

  return (
    // min-width of 82px is just enough to fit 999.99
    <div className="relative min-w-[82px]">
      {leftAligned && (
        <label style={{ left: 10 }} className={unitLabelClasses}>
          {unit}
        </label>
      )}
      <NumberInput style={inputStyle} error={error} disabled={disabled} {...props} />
      {!leftAligned && (
        <label style={{ right: 10 }} className={unitLabelClasses}>
          {unit}
        </label>
      )}
    </div>
  );
};
